import React, { useState } from 'react';
import { Shield, AlertTriangle, CheckCircle, Plus, X } from 'lucide-react';
import { register } from '../api/auth';
import C from '../constants/colors';

const EMPTY_EXTRA = { name: '', email: '' };

export default function RegisterPage({ onBack }) {
  const [form, setForm] = useState({
    tenantName: '',
    adminName: '',
    email: '',
    password: '',
    confirm: '',
    trialDays: 30,
  });
  const [extras,      setExtras]      = useState([]);   // up to 4 additional users
  const [error,       setError]       = useState('');
  const [teamCreds,   setTeamCreds]   = useState([]);   // temp passwords for extra users
  const [success,     setSuccess]     = useState(false);
  const [loading,     setLoading]     = useState(false);

  function set(field) {
    return e => setForm(f => ({ ...f, [field]: e.target.value }));
  }

  function addExtra() {
    if (extras.length < 4) setExtras(p => [...p, { ...EMPTY_EXTRA }]);
  }

  function removeExtra(i) {
    setExtras(p => p.filter((_, idx) => idx !== i));
  }

  function setExtra(i, field, value) {
    setExtras(p => p.map((e, idx) => idx === i ? { ...e, [field]: value } : e));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    // Validate extras — skip blank rows, reject partially filled
    const validExtras = [];
    for (const ex of extras) {
      if (!ex.name && !ex.email) continue; // blank row — skip
      if (!ex.name || !ex.email) {
        setError('Each team member needs both a name and an email.');
        return;
      }
      validExtras.push({ name: ex.name.trim(), email: ex.email.trim().toLowerCase() });
    }

    setLoading(true);
    try {
      const result = await register(form.tenantName, form.adminName, form.email, form.password, Number(form.trialDays), validExtras);
      setTeamCreds(result.teamCredentials || []);
      setSuccess(true);
    } catch (err) {
      const msg = err.response?.data?.error || 'Registration failed. Please try again.';
      setError(msg === 'Email already registered' ? 'This email is already in use.' : msg);
    } finally {
      setLoading(false);
    }
  }

  const inputStyle = {
    width: '100%', padding: '10px 12px', borderRadius: '8px',
    background: C.surfaceElevated, border: `1px solid ${C.border}`,
    color: C.textPrimary, fontSize: '14px', outline: 'none',
    boxSizing: 'border-box',
  };
  const labelStyle = {
    display: 'block', color: C.textSecondary, fontSize: '13px',
    fontWeight: 500, marginBottom: '6px',
  };
  const smallInput = {
    ...inputStyle, padding: '8px 10px', fontSize: '13px',
  };

  return (
    <div style={{
      minHeight: '100vh', background: C.bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'DM Sans', sans-serif", padding: '24px',
    }}>
      <div style={{ width: '100%', maxWidth: '480px' }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '56px', height: '56px', borderRadius: '16px',
            background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <Shield size={28} color="#fff" />
          </div>
          <h1 style={{ color: C.textPrimary, fontSize: '24px', fontWeight: 700, margin: 0 }}>SafeCheck</h1>
          <p style={{ color: C.textMuted, fontSize: '14px', marginTop: '6px' }}>Create your free demo environment</p>
        </div>

        <div style={{
          background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: '16px', padding: '32px',
        }}>
          {success ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <CheckCircle size={32} color={C.green} style={{ flexShrink: 0 }} />
                <div>
                  <h2 style={{ color: C.textPrimary, fontSize: '17px', fontWeight: 600, margin: 0 }}>Account created!</h2>
                  <p style={{ color: C.textSecondary, fontSize: '13px', marginTop: '3px' }}>
                    Your demo environment is ready with sample data.
                  </p>
                </div>
              </div>

              {/* Credentials table */}
              <div style={{
                background: C.surfaceElevated, border: `1px solid ${C.border}`,
                borderRadius: '10px', overflow: 'hidden', marginBottom: '20px',
              }}>
                <div style={{
                  padding: '8px 14px', borderBottom: `1px solid ${C.border}`,
                  fontSize: '11px', fontWeight: 600, color: C.textMuted,
                  textTransform: 'uppercase', letterSpacing: '0.05em',
                  display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                }}>
                  <span>Name</span><span>Email</span><span>Password</span>
                </div>
                {/* Admin row */}
                <div style={{
                  padding: '9px 14px', borderBottom: teamCreds.length ? `1px solid ${C.border}` : 'none',
                  display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', alignItems: 'center', gap: '8px',
                }}>
                  <span style={{ color: C.textPrimary, fontSize: '12px', fontWeight: 600 }}>{form.adminName} <span style={{ color: C.blue, fontWeight: 400, fontSize: '10px' }}>you</span></span>
                  <span style={{ color: C.textSecondary, fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{form.email}</span>
                  <span style={{ color: C.textMuted, fontSize: '12px', fontStyle: 'italic' }}>your password</span>
                </div>
                {teamCreds.map((tc, i) => (
                  <div key={i} style={{
                    padding: '9px 14px', borderBottom: i < teamCreds.length - 1 ? `1px solid ${C.border}` : 'none',
                    display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', alignItems: 'center', gap: '8px',
                  }}>
                    <span style={{ color: C.textPrimary, fontSize: '12px', fontWeight: 600 }}>{tc.name}</span>
                    <span style={{ color: C.textSecondary, fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tc.email}</span>
                    <span style={{
                      color: C.amber, fontSize: '12px', fontFamily: "'JetBrains Mono', monospace",
                      letterSpacing: '0.04em',
                    }}>{tc.tempPassword}</span>
                  </div>
                ))}
              </div>

              {teamCreds.length > 0 && (
                <div style={{
                  background: `${C.amber}12`, border: `1px solid ${C.amber}33`,
                  borderRadius: '8px', padding: '10px 14px', marginBottom: '20px',
                  fontSize: '12px', color: C.amber, lineHeight: 1.6,
                }}>
                  Save these temporary passwords now — they won't be shown again. Each team member should change their password after first login.
                </div>
              )}

              <button onClick={onBack} style={{
                width: '100%', padding: '12px',
                background: C.blue, border: 'none', borderRadius: '8px',
                color: '#fff', fontSize: '15px', fontWeight: 600, cursor: 'pointer',
              }}>
                Go to Sign In
              </button>
            </div>
          ) : (
            <>
              <h2 style={{ color: C.textPrimary, fontSize: '18px', fontWeight: 600, margin: '0 0 24px' }}>
                Create demo account
              </h2>

              {error && (
                <div style={{
                  background: 'rgba(239,68,68,0.1)', border: `1px solid rgba(239,68,68,0.3)`,
                  borderRadius: '8px', padding: '12px 14px',
                  display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '20px',
                }}>
                  <AlertTriangle size={16} color={C.red} style={{ flexShrink: 0, marginTop: '1px' }} />
                  <span style={{ color: C.red, fontSize: '14px' }}>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* Org & Admin */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Organisation name</label>
                  <input type="text" value={form.tenantName} onChange={set('tenantName')}
                    required placeholder="Acme Corp" style={inputStyle} />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Your name</label>
                  <input type="text" value={form.adminName} onChange={set('adminName')}
                    required placeholder="John Smith" style={inputStyle} />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Work email</label>
                  <input type="email" value={form.email} onChange={set('email')}
                    required placeholder="you@company.com" style={inputStyle} />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                  <div>
                    <label style={labelStyle}>Password</label>
                    <input type="password" value={form.password} onChange={set('password')}
                      required placeholder="Min 8 chars" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Confirm password</label>
                    <input type="password" value={form.confirm} onChange={set('confirm')}
                      required placeholder="Repeat password" style={inputStyle} />
                  </div>
                </div>

                {/* Trial length */}
                <div style={{ marginBottom: '24px' }}>
                  <label style={labelStyle}>Trial length</label>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    {[30, 60].map(days => (
                      <label key={days} style={{
                        flex: 1, display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '10px 14px', borderRadius: '8px', cursor: 'pointer',
                        border: `1px solid ${form.trialDays === days ? C.blue : C.border}`,
                        background: form.trialDays === days ? `${C.blue}18` : C.surfaceElevated,
                        color: form.trialDays === days ? C.blue : C.textSecondary,
                        fontSize: '13px', fontWeight: 500,
                      }}>
                        <input type="radio" name="trialDays" value={days}
                          checked={form.trialDays === days}
                          onChange={() => setForm(f => ({ ...f, trialDays: days }))}
                          style={{ accentColor: C.blue }} />
                        {days} days
                      </label>
                    ))}
                  </div>
                </div>

                {/* Team members */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <div>
                      <label style={{ ...labelStyle, marginBottom: 0 }}>Team members <span style={{ color: C.textMuted, fontWeight: 400 }}>(optional)</span></label>
                      <div style={{ color: C.textMuted, fontSize: '11px', marginTop: '3px' }}>
                        Add up to 4 colleagues — they'll share your password
                      </div>
                    </div>
                    {extras.length < 4 && (
                      <button type="button" onClick={addExtra} style={{
                        display: 'flex', alignItems: 'center', gap: '5px',
                        padding: '5px 10px', borderRadius: '6px',
                        border: `1px solid ${C.blue}44`, background: `${C.blue}18`,
                        color: C.blue, fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                      }}>
                        <Plus size={11} />Add
                      </button>
                    )}
                  </div>

                  {extras.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {extras.map((ex, i) => (
                        <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <input
                            type="text" value={ex.name}
                            onChange={e => setExtra(i, 'name', e.target.value)}
                            placeholder="Name" style={{ ...smallInput, flex: '0 0 140px' }}
                          />
                          <input
                            type="email" value={ex.email}
                            onChange={e => setExtra(i, 'email', e.target.value)}
                            placeholder="email@company.com" style={{ ...smallInput, flex: 1 }}
                          />
                          <button type="button" onClick={() => removeExtra(i)} style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: C.textMuted, display: 'flex', alignItems: 'center', padding: '4px', flexShrink: 0,
                          }}>
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <button type="submit" disabled={loading} className="btn" style={{
                  width: '100%', padding: '12px',
                  background: loading ? C.textMuted : C.blue,
                  border: 'none', borderRadius: '8px',
                  color: '#fff', fontSize: '15px', fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}>
                  {loading ? 'Creating account…' : 'Create demo account'}
                </button>
              </form>
            </>
          )}
        </div>

        <p style={{ textAlign: 'center', marginTop: '20px' }}>
          <span style={{ color: C.textMuted, fontSize: '13px' }}>Already have an account? </span>
          <button onClick={onBack} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: C.blue, fontSize: '13px', fontWeight: 500,
          }}>Sign in</button>
        </p>

        <p style={{ textAlign: 'center', color: C.textMuted, fontSize: '12px', marginTop: '12px' }}>
          SafeCheck LivePOB v2 · Powered by bakirsoft
        </p>
      </div>
    </div>
  );
}
